/**
 * Runtime validation at the API boundary.
 *
 * Everything below was observed against the live API on 2026-09-17, not taken
 * from the docs — the docs have been wrong three times in this project already
 * (`ids.simkl` vs `ids.simkl_id`, `to` placement on add-to-list, and the whole
 * `/discover/trending` path). A response that does not match raises rather than
 * letting `undefined` reach a screen.
 */
import { SchemaError } from "@/api/errors";

export interface SimklIds {
  /** Simkl's own id. Search returns it as `simkl_id`; sync returns `simkl`. */
  simkl: number;
  slug?: string;
  imdb?: string;
  tmdb?: string;
  tvdb?: string;
}

export interface ShowSummary {
  ids: SimklIds;
  title: string;
  year: number | null;
  poster: string | null;
  runtime: number | null;
}

export interface WatchedEpisode {
  number: number;
  watchedAt: string | null;
}

export interface WatchedSeason {
  number: number;
  episodes: WatchedEpisode[];
}

export type ShowStatus = "watching" | "plantowatch" | "completed" | "hold" | "dropped";

export interface LibraryShow {
  show: ShowSummary;
  status: ShowStatus;
  /** Pre-formatted by Simkl, e.g. "S07E06". Null before anything is watched. */
  lastWatched: string | null;
  lastWatchedAt: string | null;
  addedAt: string | null;
  /** Simkl computes the next unwatched episode; we do not derive it. */
  nextToWatch: string | null;
  watchedEpisodes: number;
  totalEpisodes: number;
  /** Non-zero means "caught up but still airing" rather than "finished". */
  notAiredEpisodes: number;
  seasons: WatchedSeason[];
  userRating: number | null;
}

function fail(path: string, detail: string): never {
  throw new SchemaError(path, detail);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function str(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function num(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/**
 * Normalises the search-vs-sync id discrepancy at the boundary.
 * No screen should ever need to know which spelling it got.
 */
export function parseIds(raw: unknown, path: string): SimklIds {
  if (!isRecord(raw)) fail(path, "ids is not an object");
  const simkl = num(raw["simkl"]) ?? num(raw["simkl_id"]);
  if (simkl === null) fail(path, "ids has neither simkl nor simkl_id");
  const ids: SimklIds = { simkl };
  const slug = str(raw["slug"]);
  if (slug !== null) ids.slug = slug;
  const imdb = str(raw["imdb"]);
  if (imdb !== null) ids.imdb = imdb;
  const tmdb = str(raw["tmdb"]);
  if (tmdb !== null) ids.tmdb = tmdb;
  const tvdb = str(raw["tvdb"]);
  if (tvdb !== null) ids.tvdb = tvdb;
  return ids;
}

export function parseShowSummary(raw: unknown, path: string): ShowSummary {
  if (!isRecord(raw)) fail(path, "show is not an object");
  const title = str(raw["title"]);
  if (title === null) fail(path, "show has no title");
  return {
    ids: parseIds(raw["ids"], path),
    title,
    year: num(raw["year"]),
    poster: str(raw["poster"]),
    runtime: num(raw["runtime"]),
  };
}

const STATUSES: ReadonlySet<string> = new Set([
  "watching",
  "plantowatch",
  "completed",
  "hold",
  "dropped",
]);

function parseSeasons(raw: unknown, path: string): WatchedSeason[] {
  if (raw === undefined || raw === null) return [];
  if (!Array.isArray(raw)) fail(path, "seasons is not an array");
  return raw.map((season): WatchedSeason => {
    if (!isRecord(season)) fail(path, "season entry is not an object");
    const number = num(season["number"]);
    if (number === null) fail(path, "season has no number");
    const rawEpisodes = season["episodes"];
    const episodes = Array.isArray(rawEpisodes)
      ? rawEpisodes.map((episode): WatchedEpisode => {
          if (!isRecord(episode)) fail(path, "episode entry is not an object");
          const epNumber = num(episode["number"]);
          if (epNumber === null) fail(path, "episode has no number");
          return { number: epNumber, watchedAt: str(episode["watched_at"]) };
        })
      : [];
    return { number, episodes };
  });
}

export function parseLibraryShow(raw: unknown, path: string): LibraryShow {
  if (!isRecord(raw)) fail(path, "library entry is not an object");
  const status = raw["status"];
  if (typeof status !== "string" || !STATUSES.has(status)) {
    fail(path, `unknown status ${JSON.stringify(status)}`);
  }
  return {
    show: parseShowSummary(raw["show"], path),
    status: status as ShowStatus,
    lastWatched: str(raw["last_watched"]),
    lastWatchedAt: str(raw["last_watched_at"]),
    addedAt: str(raw["added_to_watchlist_at"]),
    nextToWatch: str(raw["next_to_watch"]),
    watchedEpisodes: num(raw["watched_episodes_count"]) ?? 0,
    totalEpisodes: num(raw["total_episodes_count"]) ?? 0,
    notAiredEpisodes: num(raw["not_aired_episodes_count"]) ?? 0,
    seasons: parseSeasons(raw["seasons"], path),
    userRating: num(raw["user_rating"]),
  };
}

/** Simkl returns `{shows: [...]}`, or a bare `[]` when the list is empty. */
export function parseLibrary(raw: unknown, path: string): LibraryShow[] {
  if (Array.isArray(raw)) return raw.map((entry) => parseLibraryShow(entry, path));
  if (!isRecord(raw)) fail(path, "response is neither an array nor an object");
  const shows = raw["shows"];
  if (shows === undefined) return [];
  if (!Array.isArray(shows)) fail(path, "shows is not an array");
  return shows.map((entry) => parseLibraryShow(entry, path));
}

export interface SearchResult {
  ids: SimklIds;
  title: string;
  year: number | null;
  poster: string | null;
  episodeCount: number | null;
  /** Simkl's own word: "airing", "ended", "tba". */
  airStatus: string | null;
}

/**
 * Search results, whose shape differs from the library's.
 *
 * Notably `ids.simkl_id` rather than `ids.simkl` — normalised by parseIds so no
 * screen ever sees the difference.
 */
export function parseSearchResults(raw: unknown, path: string): SearchResult[] {
  if (!Array.isArray(raw)) return [];
  const results: SearchResult[] = [];
  for (const entry of raw) {
    if (!isRecord(entry)) continue;
    const title = str(entry["title"]);
    if (title === null) continue;
    results.push({
      ids: parseIds(entry["ids"], path),
      title,
      year: num(entry["year"]),
      poster: str(entry["poster"]),
      episodeCount: num(entry["ep_count"]),
      airStatus: str(entry["status"]),
    });
  }
  return results;
}

/**
 * A search result as a library show, so the sheet can preview an untracked one.
 *
 * Everything the library payload carries about progress is genuinely zero here
 * — nothing has been watched, because the show is not being tracked. The
 * episode list and the `/tv/{id}` details load exactly as they do for a
 * tracked show, so the preview is the real sheet rather than a reduced one.
 */
export function fromSearchResult(result: SearchResult): LibraryShow {
  return {
    show: {
      ids: result.ids,
      title: result.title,
      year: result.year,
      poster: result.poster,
      runtime: null,
    },
    status: "plantowatch",
    lastWatched: null,
    lastWatchedAt: null,
    addedAt: null,
    nextToWatch: null,
    watchedEpisodes: 0,
    totalEpisodes: result.episodeCount ?? 0,
    notAiredEpisodes: result.airStatus === "airing" ? 1 : 0,
    seasons: [],
    userRating: null,
  };
}
